import React, { useState, useEffect, useMemo } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import DataTable, { createTheme } from "react-data-table-component";
import { useAtom } from "jotai";
import classes from "../../assets/styles/Contact.module.css";
import {
  INVITATION,
  PENDING_MEMBER_PAYMENT,
} from "../../utils/constants/apiUrl";
import { get, deleteRecord } from "../../server";
import showToast from "../../helpers/showToast";
import { loggedInUser, teamIDCheck, isOwnerPackage } from "../../utils/store";
import Loader from "../../components/Loader";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
// import FormInput from "../../components/Forms/FormInput";
import searchIcon from "../../assets/images/global_icons/searchicon.svg";
import PendingPaymentModal from "../../components/Modals/PendingPaymentModal";
import formatPhoneNumber from "../../helpers/formatPhoneNumber";
import _ from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  elementsOptions,
  stripePromise,
} from "../../utils/constants/stripeConfig";

const emptyInvitationData = {
  email: "",
};

const PendingMemberPayments = () => {
  const [loggedUser, setLoggedUser] = useAtom(loggedInUser);
  const [userTeamId, setUserTeamId] = useAtom(teamIDCheck);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [pendingMembers, setPendingMember] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState(emptyInvitationData);
  const [isOwnerPackageActive, setIsOwnerPackageActive] =
    useAtom(isOwnerPackage);

  const handleFilter = (e) => {
    setFilterText(e.target.value);
  };

  const handleOpenModal = (id) => {
    if (!isOwnerPackageActive) {
      showToast(
        false,
        "You can not subscribe because your team is not subscribed to any package"
      );
      return;
    }
    setShowModal(true);
    setSelectedId(id);
  };

  const teamPackageInfo = useMemo(() => {
    if (!loggedUser?.package_details) return null;
    
    let pkg = loggedUser.package_details.find(
      (p) => p.team_id == userTeamId && p.is_team_owner === 1
    );
    if (pkg) return pkg;
    
    pkg = loggedUser.package_details.find((p) => 
      p.subscriptions && Array.isArray(p.subscriptions) && p.subscriptions.some(
        (sub) => sub.team_id == userTeamId && sub.is_team_owner === 1
      )
    );
    if (pkg) return pkg;

    pkg = loggedUser.package_details.find((p) => p.team_id != null);
    if (pkg) return pkg;

    pkg = loggedUser.package_details.find((p) => 
      p.subscriptions && Array.isArray(p.subscriptions) && p.subscriptions.some((sub) => sub.team_id != null)
    );
    return pkg || null;
  }, [loggedUser, userTeamId]);

  const handleCloseModal = () => {
    setSelectedData({});
    setShowModal(false);
  };

  const getPendingMember = async () => {
    setLoading(true);
    let { status, message, data } = await get(
      `${PENDING_MEMBER_PAYMENT}/${userTeamId}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    if (status) {
      setPendingMember(data);
    } else {
      showToast(status, message);
    }
    setLoading(false);
  };

  useEffect(() => {
    getPendingMember();
  }, []);

  const filteredItems = useMemo(() => {
    const filtered = pendingMembers.filter(
      (item) =>
        `${item.first_name || ""} ${item.last_name || ""}`
          .toLowerCase()
          .includes(filterText.toLowerCase()) ||
        `${item.email || ""}`
          .toLowerCase()
          .includes(filterText.toLowerCase()) ||
        `${item.city || ""}`.toLowerCase().includes(filterText.toLowerCase()) ||
        `${item.country || ""}`
          .toLowerCase()
          .includes(filterText.toLowerCase()) ||
        `${item.country_phone ? `+${item.country_phone}` : ""}${
          item.phone_number || ""
        }`.includes(filterText)
    );

    return filtered;
  }, [filterText, pendingMembers]);

  createTheme(
    "solarized",
    {
      background: {
        default: "background: var(--secondary_color)",
      },
    },
    "dark"
  );

  const columns = [
    {
      name: "Name",
      width: "200px",
      cell: (row) => (
        <p className="globalDetail">
          {row.first_name} {row.last_name}
        </p>
      ),
    },
    {
      name: "Email",
      width: "200px",
      cell: (row) => (
        <a
          href={`mailto:${row.email}`}
          className="globalDetail"
          style={{ display: "block" }}
        >
          {row.email}
        </a>
      ),
    },
    {
      name: "Phone #",
      width: "150px",
      cell: (row) => (
        <>
          {row.phone_number ? (
            <a
              href={`tel:${
                row.country_phone
                  ? "+" +
                    row.country_phone +
                    " " +
                    formatPhoneNumber(row.phone_number)
                  : row.phone_number || ""
              }`}
              className="globalDetail"
            >
              {row.country_phone
                ? "+" +
                  row.country_phone +
                  " " +
                  formatPhoneNumber(row.phone_number)
                : row.phone_number || ""}
            </a>
          ) : (
            <p className="globalDetail">-</p>
          )}
        </>
      ),
    },
    {
      width: "200px",
      name: "City, Country",
      cell: (row) => (
        <>
          {row.city || row.country ? (
            <a
              href={`http://maps.google.com/?q=${row.city || ""}${
                row.city && row.country ? ", " : ""
              }${row.country || " "}`}
              className="globalDetail"
              target="_blank"
              rel="noreferrer"
            >
              {row.city || ""}
              {row.city && row.country ? "," : ""} {row.country}
            </a>
          ) : (
            <p className="globalDetail">-</p>
          )}
        </>
      ),
    },

    {
      name: "Action",
      width: "20%",
      right: "true",
      cell: (row) => (
        <div className={classes.action}>
          <button
            className="btn customActionDropdown dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <BsThreeDotsVertical className={"globalVerticalThreeDots"} />
          </button>
          <ul className="customActionDropdownMenu dropdown-menu dropdown-menu-end ">
            <li>
              <span
                className="dropdown-item"
                onClick={() => handleOpenModal(row.id)}
              >
                Subscribe
              </span>
            </li>
            {/* <li>
              <span
                className="dropdown-item"
                style={{ color: "#E35252" }}
                onClick={() => handleDelete(row.id)}
              >
                Delete
              </span>
            </li> */}
          </ul>
        </div>
      ),
    },
  ];
  // Handle confirmation modal
  const handleShowConfirmationModal = () => setShowConfirmationModal(true);
  const handleCloseConfirmationModal = () => setShowConfirmationModal(false);

  // handle delete
  const handleDelete = (id) => {
    setDeleteId(id);
    handleShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    handleCloseConfirmationModal(true);
    setLoading(true);
    let { status, message } = await deleteRecord(
      `${INVITATION}/${deleteId}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    showToast(status, message);

    if (status) {
      await getPendingMember();
      setLoading(false);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(filteredItems.length / perPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  return (
    <>
      {!loading ? (
        <div className="">
          <div className="page_Header">
            <div className="d-flex justify-content-between align-items-md-center align-items-initial flex-md-row flex-column gap-3 mb-3">
              <div className="heading">
                <h1 className="pageTitle">
                  Pending Payments{" "}
                  <span>({filteredItems.length})</span>
                </h1>
              </div>
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <div
                  className="input-group"
                  style={{ maxWidth: "250px", height: "45px" }}
                >
                  <span className="input-group-text bg-white border-end-0">
                    <img
                      src={searchIcon}
                      alt="search"
                      style={{ height: "16px" }}
                    />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search..."
                    value={filterText}
                    onChange={handleFilter}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="default-list-table">
            <DataTable
              columns={columns}
              data={paginatedItems}
              theme="solarized"
            />
            <div className="d-flex justify-content-center align-items-center p-3 flex-wrap gap-4 border-top">
              <div className="d-flex align-items-center gap-2">
                <span className="globalDetail">Rows per page:</span>
                <select
                  className="form-select"
                  style={{ width: "auto", padding: "6px 35px 6px 12px" }}
                  value={perPage}
                  onChange={(e) => handlePerRowsChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      style={{
                        padding: "8px 16px",
                        minWidth: "45px",
                        textAlign: "center",
                        color: "#58a388",
                      }}
                    >
                      &laquo; Previous
                    </button>
                  </li>

                  {(() => {
                    const pages = [];
                    if (totalPages <= 0) {
                      // no data yet
                    } else if (totalPages <= 8) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (currentPage <= 4) {
                        for (let i = 1; i <= 6; i++) pages.push(i);
                        pages.push("ellipsis-end");
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        pages.push(1);
                        pages.push("ellipsis-start");
                        for (let i = totalPages - 5; i <= totalPages; i++)
                          pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push("ellipsis-start");
                        pages.push(currentPage - 1);
                        pages.push(currentPage);
                        pages.push(currentPage + 1);
                        pages.push("ellipsis-end");
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (typeof page === "string") {
                        return (
                          <li
                            key={`ellipsis-${index}`}
                            className="page-item disabled"
                          >
                            <span
                              className="page-link"
                              style={{
                                padding: "8px 16px",
                                minWidth: "45px",
                                textAlign: "center",
                                color: "#58a388",
                              }}
                            >
                              ...
                            </span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={page}
                          className={`page-item ${currentPage === page ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                            style={{
                              backgroundColor: currentPage === page ? "#58a388" : "",
                              borderColor: currentPage === page ? "#58a388" : "",
                              color: currentPage === page ? "#fff" : "#58a388",
                              padding: "8px 16px",
                              minWidth: "45px",
                              textAlign: "center",
                            }}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    });
                  })()}

                  <li
                    className={`page-item ${
                      currentPage === totalPages || filteredItems.length === 0
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      style={{
                        padding: "8px 16px",
                        minWidth: "45px",
                        textAlign: "center",
                        color: "#58a388",
                      }}
                    >
                      Next &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <ConfirmationModal
            show={showConfirmationModal}
            handleCloseModal={handleCloseConfirmationModal}
            handleConfirmDelete={handleConfirmDelete}
            confirmTitle="invitation"
          />
        </div>
      ) : (
        <Loader />
      )}

      <Elements stripe={stripePromise} options={elementsOptions}>
        <PendingPaymentModal
          show={showModal}
          stripePromise={stripePromise}
          data={selectedData}
          setSelectedData={setSelectedData}
          userTeamId={userTeamId}
          handleCloseModal={handleCloseModal}
          loading={modalLoading}
          selectedId={selectedId}
          pendingMembers={pendingMembers}
          setPendingMember={setPendingMember}
          teamPackageInfo={teamPackageInfo}
        />
      </Elements>
    </>
  );
};

export default PendingMemberPayments;
